import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
const styles = require('./Dropdown.module.scss');

type dropDownOption = {
  value: string;
  label: string;
};

interface Props {
  placeHolder: string;
  options: dropDownOption[];
  selectedData?: dropDownOption[];
  onChange: (newValue: dropDownOption[]) => void;
  // When set, the dropdown watches this element's `change` event and adds the
  // country of every office location to the selection, mapped to its canonical
  // name via `countryNames`. Only the eligible-locations field opts in (ILJ-1391).
  officeLocationsSelector?: string;
  countryNames?: Record<string, string>;
}

const Dropdown = ({
  placeHolder,
  options,
  selectedData,
  onChange,
  officeLocationsSelector,
  countryNames,
}: Props) => {
  const [showMenu, setShowMenu] = useState(false);
  const [selectedValue, setSelectedValue] = useState<Array<dropDownOption>>(selectedData || []);
  const [searchValue, setSearchValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const highlightedRef = useRef<HTMLDivElement>(null);
  // Latest selection, read inside the office-sync listener without re-subscribing
  // on every keystroke (the listener is attached once).
  const selectedValueRef = useRef(selectedValue);
  selectedValueRef.current = selectedValue;

  // Add each office location's country to the eligible list as soon as an office
  // is picked, instead of only after a save + step re-render (ILJ-1391). Adds
  // only: removing an office still prunes its country server-side on save.
  useEffect(() => {
    if (!officeLocationsSelector) return undefined;

    const el = document.querySelector(officeLocationsSelector) as HTMLInputElement | null;
    if (!el) return undefined;

    const syncFromOffices = () => {
      let offices: Array<{ country_code?: string }> = [];
      try {
        offices = JSON.parse(el.value || '[]');
      } catch {
        return;
      }

      const officeOptions = offices
        .map(office => office.country_code && countryNames?.[office.country_code])
        .filter((name): name is string => Boolean(name))
        .map(name => ({ value: name.toLowerCase(), label: name }));

      // De-dupe both against the current selection and within the office set
      // itself, so several offices in the same country add a single tag.
      const current = selectedValueRef.current;
      const seen = new Set(current.map(option => option.value));
      const additions: dropDownOption[] = [];
      officeOptions.forEach(option => {
        if (seen.has(option.value)) return;
        seen.add(option.value);
        additions.push(option);
      });
      if (additions.length === 0) return;

      const merged = [...current, ...additions];
      setSelectedValue(merged);
      onChange(merged);
    };

    el.addEventListener('change', syncFromOffices);
    return () => el.removeEventListener('change', syncFromOffices);
  }, [officeLocationsSelector, countryNames, onChange]);

  // Clear the selection when the wizard resets the whole location step (the user
  // changed work model — ILJ-1407). onChange keeps the parent SelectWithTags and
  // its hidden inputs in sync.
  useEffect(() => {
    const reset = () => {
      setSelectedValue([]);
      setSearchValue('');
      onChange([]);
    };

    document.addEventListener('location-details:reset', reset);
    return () => document.removeEventListener('location-details:reset', reset);
  }, [onChange]);

  useEffect(() => {
    setSearchValue('');
    if (showMenu && searchRef.current) {
      searchRef.current.focus();
    }
  }, [showMenu]);

  // Reset the keyboard highlight whenever the list of visible options changes
  // (menu opened/closed or the search term narrowed the results).
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [showMenu, searchValue]);

  // Keep the keyboard-highlighted option scrolled into view (the timezone list
  // is long enough to overflow the menu).
  useEffect(() => {
    highlightedRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [highlightedIndex]);

  useEffect(() => {
    const handler = e => {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };

    window.addEventListener('click', handler);
    return () => {
      window.removeEventListener('click', handler);
    };
  });

  const handleInputClick = () => {
    setShowMenu(!showMenu);
  };

  const getDisplay = () => (
    <div className={styles.dropdownTags}>
      {selectedValue.map(option => (
        <div key={option.value} className={styles.dropdownTagItem}>
          <span className={styles.dropdownTagLabel}>{option.label}</span>
          <span onClick={e => onTagRemove(e, option)} className={styles.dropdownTagClose} />
        </div>
      ))}
    </div>
  );

  const removeOption = (option: dropDownOption) =>
    selectedValue.filter(o => o.value !== option.value);

  const onTagRemove = (e, option: dropDownOption) => {
    e.stopPropagation();
    const newValue = removeOption(option);
    setSelectedValue(newValue);
    onChange(newValue);
  };

  const onItemClick = (option: dropDownOption) => {
    let newValue: React.SetStateAction<dropDownOption[]>;

    if (selectedValue.findIndex(o => o.value === option.value) >= 0) {
      newValue = removeOption(option);
    } else {
      newValue = [...selectedValue, option];
    }
    setSelectedValue(newValue);
    onChange(newValue);
  };

  const isSelected = (option: dropDownOption) =>
    selectedValue.filter(o => o.value === option.value).length > 0;

  const onSearch = (e: { target: { value: React.SetStateAction<string> } }) =>
    setSearchValue(e.target.value);

  const getOptions = () => {
    if (!searchValue) {
      return options;
    }

    return options.filter(
      option => option.label.toLowerCase().indexOf(searchValue.toLowerCase()) >= 0
    );
  };

  // Options actually rendered in the menu: filtered by the search term and
  // excluding anything already picked. Keyboard navigation indexes into this
  // exact list so the highlight lines up with what the user sees.
  const visibleOptions = getOptions().filter(option => !isSelected(option));

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!showMenu) {
        setShowMenu(true);
        return;
      }
      setHighlightedIndex(index => Math.min(index + 1, visibleOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(index => Math.max(index - 1, 0));
    } else if (e.key === 'Enter') {
      // While the dropdown is open, Enter must act on the list — never bubble up
      // to submit the surrounding wizard/edit form (ILJ-1316/1320).
      if (showMenu) {
        e.preventDefault();
        e.stopPropagation();
        const option = visibleOptions[highlightedIndex];
        if (option) {
          onItemClick(option);
          // Mirror the mouse path: selecting via Enter clears the typed search
          // and closes the menu. Mouse clicks get both for free through the
          // click-outside handler; keyboard selection does not, so the menu would
          // otherwise stay open (ILJ-1330/1390).
          setSearchValue('');
          setShowMenu(false);
        }
      }
    } else if (e.key === 'Escape') {
      setShowMenu(false);
    }
  };

  return (
    <>
      <div
        className={classNames(styles.dropdownContainer, {
          [styles.dropdownContainerOpen]: showMenu,
        })}
      >
        <div
          ref={inputRef}
          onClick={handleInputClick}
          className={styles.searchBox}
          data-testid='search-input'
        >
          <input
            onChange={onSearch}
            onKeyDown={onKeyDown}
            value={searchValue}
            ref={searchRef}
            placeholder={placeHolder}
          />
          <span className={styles.chevron} />
        </div>
        {showMenu && options.length && (
          <div className={styles.dropdownMenu} data-testid='dropdown'>
            {visibleOptions.map((option, index) => (
              <div
                onClick={() => onItemClick(option)}
                ref={index === highlightedIndex ? highlightedRef : null}
                key={option.value}
                className={classNames(styles.dropdownItem, {
                  [styles.dropdownItemHighlighted]: index === highlightedIndex,
                })}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className={styles.dropdownSelectedValue}>{getDisplay()}</div>
    </>
  );
};

export default Dropdown;
